import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, Link2, RefreshCw, Ban, Megaphone, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { copyText, rateUrl, requestMessage, shareRequest } from "@/lib/ratings";

const STATUS_STYLE = { active: "#22c55e", revoked: "#ef4444", expired: "#f59e0b", suspended: "#ef4444", exhausted: "#f59e0b" };
const btn = "rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5 glass hover:scale-[1.03] transition-transform";

export default function CommunityRequestCard({ name }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expires, setExpires] = useState("");
  const [maxUses, setMaxUses] = useState("");

  const load = useCallback(() => api.get("/ratings/links").then(({ data }) => setD(data)).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const body = {};
      if (expires) body.expires_days = Number(expires);
      if (maxUses) body.max_uses = Number(maxUses);
      await api.post("/ratings/links", body);
      toast.success("New rating-request link generated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const revoke = async (id) => {
    await api.post(`/ratings/links/${id}/revoke`).catch(() => {});
    toast.success("Link revoked");
    load();
  };

  if (d === null) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>;
  if (d === false) return null;
  const a = d.active;
  const url = a?.token ? rateUrl(a.token) : null;

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="community-request-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-bold flex items-center gap-2"><Megaphone className="w-4 h-4 text-primary-c" /> Community Rating Request</div>
          <div className="text-sm mt-1" style={{ color: "var(--text-2)" }}><b data-testid="ratings-received" style={{ color: "var(--text)" }}>{d.ratings_received}</b> ratings received · {d.via_links} via links</div>
        </div>
        {a && <span className="text-[10px] mono uppercase tracking-wider rounded-full px-2 py-0.5 glass" style={{ color: STATUS_STYLE[a.status] }} data-testid="link-status">Link: {a.status}</span>}
      </div>

      {!d.verified ? (
        <div className="mt-4 text-xs flex gap-2" style={{ color: "#f59e0b" }} data-testid="request-not-verified">
          <ShieldAlert className="w-4 h-4 shrink-0" /> Verify your email and add a working Facebook profile link in your profile to request community ratings.
        </div>
      ) : !a ? (
        <button data-testid="request-community-ratings-btn" onClick={generate} disabled={busy} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold mt-4 flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Request Community Ratings
        </button>
      ) : (
        <>
          <div className="mt-4 rounded-xl glass px-3 py-2 text-xs mono break-all" data-testid="rating-link-url">{url}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="copy-rating-request-btn" className="btn-glow rounded-full px-4 py-2 text-xs font-bold flex items-center gap-1.5"
              onClick={async () => { await copyText(requestMessage(name, url)); toast.success("Rating request copied — paste it into Messenger, Viber, SMS…"); }}>
              <Copy className="w-3.5 h-3.5" /> Copy Rating Request
            </button>
            <button data-testid="share-profile-btn" className={btn} onClick={async () => { const r = await shareRequest(name, url); if (r === "copied") toast.success("Sharing isn't available here — message copied instead"); }}>
              <Share2 className="w-3.5 h-3.5 text-primary-c" /> Share Profile
            </button>
            <button data-testid="copy-link-btn" className={btn} onClick={async () => { await copyText(url); toast.success("Link copied"); }}><Link2 className="w-3.5 h-3.5 text-primary-c" /> Copy Link</button>
            <button data-testid="generate-new-link-btn" className={btn} onClick={generate} disabled={busy}><RefreshCw className="w-3.5 h-3.5 text-primary-c" /> Generate New Link</button>
            <button data-testid="revoke-link-btn" className={btn} onClick={() => revoke(a.link_id)}><Ban className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /> Revoke Link</button>
          </div>
          <div className="text-[11px] mt-2" style={{ color: "var(--text-2)" }}>
            {a.expires_at ? `Expires ${a.expires_at.slice(0, 10)}` : "No expiry"} · {a.uses_count}{a.max_uses ? `/${a.max_uses}` : ""} submissions. Recipients must still sign in and be verified.
          </div>
        </>
      )}

      {d.verified && (
        <div className="flex flex-wrap gap-2 mt-4 text-xs items-center" style={{ color: "var(--text-2)" }}>
          <span>New link options:</span>
          <select data-testid="link-expiry-select" value={expires} onChange={(e) => setExpires(e.target.value)} className="rounded-lg glass px-2 py-1" style={{ color: "var(--text)" }}>
            <option value="">No expiry</option><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option>
          </select>
          <select data-testid="link-maxuses-select" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="rounded-lg glass px-2 py-1" style={{ color: "var(--text)" }}>
            <option value="">Unlimited ratings</option><option value="10">Max 10</option><option value="25">Max 25</option><option value="50">Max 50</option>
          </select>
        </div>
      )}

      {d.history.length > 0 && (
        <details className="mt-4" data-testid="link-history">
          <summary className="text-xs font-bold cursor-pointer" style={{ color: "var(--text-2)" }}>Rating Request History ({d.history.length})</summary>
          <div className="mt-2 divide-y text-xs" style={{ borderColor: "var(--border)" }}>
            {d.history.map((l) => (
              <div key={l.link_id} className="py-2 flex items-center justify-between gap-2" data-testid={`link-row-${l.link_id}`}>
                <span className="mono">{l.created_at.slice(0, 10)}</span>
                <span>{l.uses_count} ratings</span>
                <span className="mono uppercase" style={{ color: STATUS_STYLE[l.status] }}>{l.status}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
